import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { authenticatedGet, authenticatedPost, authenticatedPut } from '@/utils/api'
import { useRootStore } from './root'
import { useAuthStore } from './auth'
import { useArchivesStore } from './archives'
import { useErrorHandler } from '@/composables/useErrorHandler'
import { assertPerimeterSelection } from '@/utils/perimeterValidation'

// Types
export interface Client {
  code: string
  name: string
}

export interface Portfolio {
  code: string
  name: string
}

export interface Compartment {
  code: string
  name: string
  portfolioCode?: string
  portfolioName?: string
}

interface PortfolioApiItem {
  codePortefeuille?: string
  libellePortefeuille?: string
}

interface CompartmentApiItem {
  codeCompartiment?: string
  libelleCompartiment?: string
  codePortefeuille?: string
  libellePortefeuille?: string
}

interface CodeAndNameApi {
  code?: string
  name?: string
  Code?: string
  Name?: string
}

// Réponse de GET /api/arago/perimeter : camelCase ou PascalCase selon la sérialisation
interface SavedPerimeterApi {
  rolename?: string
  Rolename?: string
  portfolios?: CodeAndNameApi[]
  Portfolios?: CodeAndNameApi[]
  valuationDate?: string
  ValuationDate?: string
  compartments?: CodeAndNameApi[]
  Compartments?: CodeAndNameApi[]
  lookThroughLevel?: number
  LookThroughLevel?: number
  transparencyLevel?: number
}

function toCodeAndNames(items: CodeAndNameApi[] | undefined): { code: string; name: string }[] {
  if (!Array.isArray(items)) return []
  return items.map(item => {
    const code = item.Code || item.code || ''
    return { code, name: item.Name || item.name || code }
  })
}

/**
 * Objets de la liste disponible correspondant aux codes enregistrés (sans casse) ; à défaut,
 * les données enregistrées telles quelles.
 */
function pickFromList<T extends { code: string }>(available: T[], saved: { code: string; name: string }[]): (T | { code: string; name: string })[] {
  const codes = saved.map(s => s.code.toLowerCase())
  const matching = available.filter(a => codes.includes(a.code.toLowerCase()))
  return matching.length > 0 ? matching : saved
}

export const usePerimeterStore = defineStore('perimeter', () => {
  const rootStore = useRootStore()
  const authStore = useAuthStore()
  const archivesStore = useArchivesStore()
  const { handleError } = useErrorHandler()

  // Cache TTL (5 minutes)
  const CACHE_TTL = 5 * 60 * 1000
  const rolenamesCache = ref<{ data: Client[], timestamp: number } | null>(null)
  const portfoliosCache = ref<{ data: Portfolio[], timestamp: number, clientCode: string } | null>(null)

  // State - Flags
  const perimeterChanged = ref(false)
  const perimeterLoaded = ref(false)
  const loadingFromDatabase = ref(true)
  const isValid = ref(true)
  const isLoading = ref(false)
  const isLoadingPortfolios = ref(false)
  const error = ref<string | null>(null)
  // Périmètre restauré depuis le dernier enregistrement (message et bouton « Réinitialiser » du formulaire)
  const restoredFromApi = ref(false)

  // State - Lists
  const rolenames = ref<Client[]>([])
  const client = ref<Client | undefined>(undefined)
  const portfoliosList = ref<Portfolio[]>([])
  const portfolios = ref<Portfolio[]>([])
  const datesList = ref<string[]>([])
  const datesByPortfolio = ref<Record<string, string[]>>({})
  const valuationDate = ref<string | undefined>(undefined)
  const compartmentsList = ref<Compartment[]>([])
  const compartments = ref<Compartment[]>([])
  const transparencyLevel = ref<number | undefined>(undefined)

  // Computed
  const portfoliosNameList = computed(() => {
    return portfolios.value.map(p => p.name).join(', ')
  })

  const compartmentsNameList = computed(() => {
    return compartments.value.map(c => c.name).join(', ')
  })

  const isPerimeterEmpty = computed(() => {
    return portfolios.value.length === 0 && 
           valuationDate.value === undefined && 
           compartments.value.length === 0 && 
           transparencyLevel.value === undefined
  })

  const rolenameEmpty = computed(() => {
    return !client.value || client.value.code === ''
  })

  const currentPerimeter = computed(() => {
    if (isPerimeterEmpty.value) return null
    
    return {
      rolename: client.value?.code,
      portfolios: portfolios.value,
      valuationDate: valuationDate.value,
      compartments: compartments.value,
      transparencyLevel: transparencyLevel.value,
    }
  })

  // Actions
  
  /**
   * Charge la liste des clients (rolenames)
   */
  async function loadRolenames() {
    // Vérifier le cache
    if (rolenamesCache.value && Date.now() - rolenamesCache.value.timestamp < CACHE_TTL) {
      rolenames.value = rolenamesCache.value.data
      return
    }

    try {
      const baseUrl = rootStore.baseUrl
      
      if (authStore.isAdmin) {
        const response = await authenticatedGet<Client[]>(baseUrl + '/api/inventory/all-rolenames')
        rolenames.value = response.data.filter(
          (role: Client) => 
            role.code.includes('Client.') || 
            role.code.includes('FDLCCP') || 
            role.code.includes('fdlc')
        )
      } else {
        const account = authStore.userAccount
        if (!account) {
          rolenames.value = []
          return
        }
        
        const response = await authenticatedGet<Client[]>(baseUrl + '/api/inventory/rolenames', {
          params: {
            userID: account.localAccountId || account.homeAccountId,
          },
        })
        rolenames.value = response.data
      }

      // Mettre en cache
      rolenamesCache.value = { data: rolenames.value, timestamp: Date.now() }
    } catch (err) {
      handleError(err, { module: 'Périmètre', action: 'loadRolenames' }, 'Impossible de charger la liste des clients')
      rolenames.value = []
      throw err
    }
  }

  /**
   * Charge le client par défaut pour un utilisateur non-admin
   */
  async function loadDefaultClient() {
    try {
      const baseUrl = rootStore.baseUrl
      const account = authStore.userAccount
      
      if (!account) return
      
      const response = await authenticatedGet<Client[]>(baseUrl + '/api/inventory/rolenames', {
        params: {
          userID: account.localAccountId || account.homeAccountId,
        },
      })
      
      const defaultClient = response.data.find((e: Client) => e.code !== 'Solvency')
      
      if (defaultClient) {
        client.value = defaultClient
        await loadPortfolios()
        
        // Charger les archives pour le client par défaut
        await archivesStore.getArchives()
      }
    } catch (err) {
      handleError(err, { module: 'Périmètre', action: 'loadDefaultClient' }, 'Impossible de charger le client par défaut')
      client.value = undefined
    }
  }

  /**
   * Charge la liste des portefeuilles pour le client sélectionné
   */
  async function loadPortfolios() {
    if (!client.value) {
      portfoliosList.value = []
      return
    }

    // Vérifier le cache
    const cacheKey = client.value.code
    if (portfoliosCache.value && 
        portfoliosCache.value.clientCode === cacheKey &&
        Date.now() - portfoliosCache.value.timestamp < CACHE_TTL) {
      portfoliosList.value = portfoliosCache.value.data
      return
    }

    isLoadingPortfolios.value = true
    try {
      const baseUrl = rootStore.baseUrl
      const account = authStore.userAccount
      
      if (!account) {
        portfoliosList.value = []
        return
      }

      const response = await authenticatedGet<PortfolioApiItem[]>(baseUrl + '/api/inventory/portfolios', {
        params: {
          rolename: client.value.code,
          userID: account.localAccountId || account.homeAccountId,
        },
      })
      
      portfoliosList.value = response.data.map((portfolio) => ({
        code: portfolio.codePortefeuille || '',
        name: portfolio.libellePortefeuille || portfolio.codePortefeuille || '[Sans libellé]',
      }))

      // Mettre en cache
      portfoliosCache.value = { 
        data: portfoliosList.value, 
        timestamp: Date.now(),
        clientCode: client.value.code
      }
    } catch (err) {
      console.error('Erreur chargement portfolios:', err)
      portfoliosList.value = []
      throw err
    } finally {
      isLoadingPortfolios.value = false
    }
  }

  /**
   * Charge les dates de valorisation disponibles pour les portefeuilles sélectionnés,
   * en retournant la couverture par portefeuille (pour les indicateurs du calendrier).
   */
  async function loadDates() {
    if (portfolios.value.length === 0) {
      datesList.value = []
      datesByPortfolio.value = {}
      return
    }

    try {
      const baseUrl = rootStore.baseUrl
      const portfolioCodes = portfolios.value.map(p => p.code)

      const response = await authenticatedPost<Record<string, string[]>>(
        baseUrl + '/api/inventory/valuation-dates-by-portfolio',
        { portfolioCodes },
      )

      datesByPortfolio.value = response.data
      // Union de toutes les dates disponibles (pour la navigation dans le calendrier)
      datesList.value = [...new Set(Object.values(response.data).flat())]
    } catch (err) {
      console.error('Erreur chargement dates:', err)
      datesList.value = []
      datesByPortfolio.value = {}
      throw err
    }
  }

  /**
   * Charge les compartiments disponibles pour les portefeuilles et date sélectionnés
   */
  async function loadCompartments() {
    if (portfolios.value.length === 0 || !valuationDate.value) {
      compartmentsList.value = []
      return
    }

    try {
      const baseUrl = rootStore.baseUrl
      const portfolioCodes = portfolios.value.map(p => p.code)

      const response = await authenticatedPost<CompartmentApiItem[]>(baseUrl + '/api/inventory/compartments', {
        portfolioCodes: portfolioCodes,
        valuationDate: valuationDate.value,
      })
      
      compartmentsList.value = response.data.map((compartment) => ({
        code: compartment.codeCompartiment || '',
        name: compartment.libelleCompartiment || compartment.codeCompartiment || '[Sans libellé]',
        portfolioCode: compartment.codePortefeuille,
        portfolioName: compartment.libellePortefeuille || compartment.codePortefeuille,
      }))
    } catch (err) {
      console.error('Erreur chargement compartments:', err)
      compartmentsList.value = []
      throw err
    }
  }

  /**
   * Restaure le dernier périmètre enregistré de l'utilisateur (appelé par le routeur à la connexion,
   * quand la session n'en contient pas). Les portefeuilles et compartiments sont repris depuis les
   * listes disponibles pour avoir les objets complets (libellés, portefeuille de rattachement).
   * @returns true si un périmètre a été restauré
   */
  async function loadPerimeterFromApi(): Promise<boolean> {
    const account = authStore.userAccount
    if (!account) {
      console.warn('loadPerimeterFromApi: Aucun compte utilisateur')
      return false
    }

    try {
      const baseUrl = rootStore.baseUrl
      const response = await authenticatedGet<SavedPerimeterApi | null>(baseUrl + '/api/arago/perimeter', {
        params: { userID: account.localAccountId || account.homeAccountId },
      })

      // L'API peut renvoyer les propriétés en camelCase ou en PascalCase
      const data = response.data
      const rolename = data?.rolename || data?.Rolename
      if (!data || !rolename) {
        return false
      }

      // Nom réel du rôle depuis la liste des rôles (repli sur le code)
      await loadRolenames()
      client.value = rolenames.value.find(r => r.code.toLowerCase() === rolename.toLowerCase())
        || { code: rolename, name: rolename }

      await loadPortfolios()
      const savedPortfolios = toCodeAndNames(data.portfolios || data.Portfolios)
      portfolios.value = pickFromList(portfoliosList.value, savedPortfolios)

      valuationDate.value = data.valuationDate || data.ValuationDate

      await Promise.all([
        portfolios.value.length > 0 ? loadDates() : Promise.resolve(),
        valuationDate.value ? loadCompartments() : Promise.resolve(),
      ])
      const savedCompartments = toCodeAndNames(data.compartments || data.Compartments)
      compartments.value = pickFromList(compartmentsList.value, savedCompartments)

      const level = data.lookThroughLevel ?? data.LookThroughLevel ?? data.transparencyLevel
      if (level !== undefined && level !== null) {
        transparencyLevel.value = level
      }

      perimeterLoaded.value = true
      perimeterChanged.value = false
      restoredFromApi.value = true
      return true
    } catch (err) {
      console.warn('loadPerimeterFromApi: Aucun périmètre sauvegardé ou erreur', err)
      return false
    }
  }

  /**
   * Réinitialise la sélection restaurée (bouton « Réinitialiser » du formulaire) en gardant le rôle.
   */
  function resetPerimeter() {
    portfolios.value = []
    valuationDate.value = undefined
    compartments.value = []
    transparencyLevel.value = undefined
    restoredFromApi.value = false
  }

  /**
   * Remplace le nom du rôle par son libellé réel (utile après une restauration de session où le
   * nom peut valoir le code).
   */
  function syncClientName() {
    const current = client.value
    if (!current?.code) return

    const matching = rolenames.value.find(r => r.code.toLowerCase() === current.code.toLowerCase())
    if (matching && (matching.name !== current.name || matching.code !== current.code)) {
      client.value = matching
    }
  }

  /**
   * Sauvegarde le périmètre actuel en base de données
   */
  async function savePerimeter() {
    const account = authStore.userAccount
    if (!account) {
      return
    }

    assertPerimeterSelection(
      {
        client: client.value,
        portfolios: portfolios.value,
        valuationDate: valuationDate.value,
        compartments: compartments.value,
        lookThroughLevel: transparencyLevel.value,
      },
      'Impossible de sauvegarder un perimetre incomplet'
    )

    try {
      const baseUrl = rootStore.baseUrl
      const userId = account.localAccountId || account.homeAccountId
      
      await authenticatedPut(baseUrl + '/api/arago/perimeter', {
        userID: userId,
        rolename: client.value?.code,
        portfolios: portfolios.value.map(p => p.code),
        valuationDate: valuationDate.value,
        compartments: compartments.value.map(c => c.code),
        lookThroughLevel: transparencyLevel.value,
      })

      perimeterChanged.value = false
      perimeterLoaded.value = true
    } catch (err) {
      handleError(err, { module: 'Périmètre', action: 'savePerimeter' }, 'Impossible de sauvegarder le périmètre')
      throw err
    }
  }

  /**
   * Définit le client sélectionné
   */
  function setClient(newClient: Client | undefined) {
    client.value = newClient
    
    if (!loadingFromDatabase.value) {
      portfolios.value = []
      valuationDate.value = undefined
      compartments.value = []
      perimeterChanged.value = true
      
      // Marquer les résultats comme obsolètes si on modifie le périmètre
      import('./simulation').then(({ useSimulationStore }) => {
        const simStore = useSimulationStore()
        if (!simStore.isRestoringAfterSimulation) {
          simStore.setResultsObsolete(true)
        }
      })
    }
    
    // Charger les archives pour le nouveau client, sans attendre ; getArchives journalise déjà
    // l'erreur avant de la relancer : on l'intercepte pour éviter un rejet non géré.
    if (newClient?.code) {
      archivesStore.getArchives().catch(() => undefined)
    }
  }

  /**
   * Définit les portefeuilles sélectionnés
   */
  function setPortfolios(newPortfolios: Portfolio[]) {
    portfolios.value = newPortfolios
    perimeterChanged.value = true
    
    // Marquer les résultats comme obsolètes si on modifie le périmètre
    import('./simulation').then(({ useSimulationStore }) => {
      const simStore = useSimulationStore()
      if (!simStore.isRestoringAfterSimulation) {
        simStore.setResultsObsolete(true)
      }
    })
  }

  /**
   * Définit la date de valorisation
   */
  function setValuationDate(date: string | undefined) {
    valuationDate.value = date
    perimeterChanged.value = true
    
    // Marquer les résultats comme obsolètes si on modifie le périmètre
    import('./simulation').then(({ useSimulationStore }) => {
      const simStore = useSimulationStore()
      if (!simStore.isRestoringAfterSimulation) {
        simStore.setResultsObsolete(true)
      }
    })
  }

  /**
   * Définit les compartiments sélectionnés
   */
  function setCompartments(newCompartments: Compartment[]) {
    compartments.value = newCompartments
    perimeterChanged.value = true
    
    // Marquer les résultats comme obsolètes si on modifie le périmètre
    import('./simulation').then(({ useSimulationStore }) => {
      const simStore = useSimulationStore()
      if (!simStore.isRestoringAfterSimulation) {
        simStore.setResultsObsolete(true)
      }
    })
  }

  /**
   * Définit le niveau de transparence
   */
  function setTransparencyLevel(level: number | undefined) {
    transparencyLevel.value = level
    perimeterChanged.value = true
    
    // Marquer les résultats comme obsolètes si on modifie le périmètre
    import('./simulation').then(({ useSimulationStore }) => {
      const simStore = useSimulationStore()
      if (!simStore.isRestoringAfterSimulation) {
        simStore.setResultsObsolete(true)
      }
    })
  }

  /**
   * Réinitialise le store
   */
  function reset() {
    perimeterChanged.value = false
    perimeterLoaded.value = false
    loadingFromDatabase.value = true
    isValid.value = true
    rolenames.value = []
    client.value = undefined
    portfoliosList.value = []
    portfolios.value = []
    datesList.value = []
    datesByPortfolio.value = {}
    valuationDate.value = undefined
    compartmentsList.value = []
    compartments.value = []
    transparencyLevel.value = undefined
    isLoading.value = false
    isLoadingPortfolios.value = false
    error.value = null
    restoredFromApi.value = false
  }

  return {
    // State
    perimeterChanged,
    perimeterLoaded,
    loadingFromDatabase,
    isValid,
    isLoading,
    isLoadingPortfolios,
    error,
    restoredFromApi,
    rolenames,
    client,
    portfoliosList,
    portfolios,
    datesList,
    datesByPortfolio,
    valuationDate,
    compartmentsList,
    compartments,
    transparencyLevel,
    // Computed
    portfoliosNameList,
    compartmentsNameList,
    isPerimeterEmpty,
    rolenameEmpty,
    currentPerimeter,
    // Actions
    loadRolenames,
    loadDefaultClient,
    loadPortfolios,
    loadDates,
    loadCompartments,
    loadPerimeterFromApi,
    resetPerimeter,
    syncClientName,
    savePerimeter,
    setClient,
    setPortfolios,
    setValuationDate,
    setCompartments,
    setTransparencyLevel,
    reset,
  }
}, {
  persist: {
    key: 'arago-perimeter',
    storage: sessionStorage, // sessionStorage pour sécurité (données métier sensibles)
    pick: ['client', 'portfolios', 'valuationDate', 'compartments', 'transparencyLevel', 'perimeterLoaded', 'restoredFromApi'],
  },
})
